import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { hubUrl } from "../../lib/publicUrl";
import { Store, ShoppingBag, BookOpen, Heart, Video, Calendar, Share2, Users, ArrowRight, ExternalLink, Copy, TrendingUp, Sparkles, AlertTriangle, BarChart3 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const ICONS = { trending: TrendingUp, sparkles: Sparkles, alert: AlertTriangle, ai: Sparkles };
const TONES = {
  success: "bg-success-soft text-success border-emerald-200",
  ai:      "bg-ai-soft text-ai border-violet-200",
  info:    "bg-info-soft text-info border-blue-200",
  warning: "bg-warning-soft text-warning border-amber-200",
};

export default function Storefront() {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [insights, setInsights] = useState(null);
  const url = hubUrl((user?.workspace_id || "ws").slice(-8));

  useEffect(() => {
    api.get("/products").then(r => setProducts(r.data)).catch(()=>{});
    api.get("/commerce/insights").then(r => setInsights(r.data)).catch(()=>{});
  }, []);

  const copy = () => { navigator.clipboard.writeText(url); toast.success("Storefront link copied"); };

  const sections = [
    { to: "/commerce/products",    label: "Products",    icon: ShoppingBag,   count: products.length || 3, gradient: "from-violet-500 to-fuchsia-500" },
    { to: "/commerce/courses",     label: "Courses",     icon: BookOpen,      count: 3, gradient: "from-fuchsia-500 to-pink-500" },
    { to: "/commerce/memberships", label: "Memberships", icon: Heart,         count: 2, gradient: "from-pink-500 to-rose-500" },
    { to: "/commerce/webinars",    label: "Webinars",    icon: Video,         count: 3, gradient: "from-rose-500 to-orange-500" },
    { to: "/commerce/bookings",    label: "Bookings",    icon: Calendar,      count: 4, gradient: "from-orange-500 to-amber-500" },
    { to: "/commerce/affiliates",  label: "Affiliates",  icon: Share2,        count: 5, gradient: "from-amber-500 to-yellow-500" },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-10" data-testid="storefront-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-10 w-10 rounded-2xl ashra-gradient-bg grid place-items-center shadow-sm">
          <Store className="h-5 w-5 text-white" />
        </div>
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight text-ink">Storefront</h1>
          <p className="text-sm text-meta mt-0.5">AI-powered monetization infrastructure — one link, every offer.</p>
        </div>
      </div>

      {/* Public URL */}
      <div className="rounded-3xl ashra-gradient-bg p-[1px] mb-6">
        <div className="rounded-3xl bg-white p-5 sm:p-6 flex items-center gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <p className="text-[11px] uppercase tracking-wider text-meta font-bold">Public storefront URL</p>
            <p className="font-mono text-sm sm:text-base font-semibold text-ink truncate mt-1">{url}</p>
          </div>
          <button onClick={copy} className="rounded-full border border-line bg-white px-3 py-2 text-xs font-medium text-ink-soft hover:bg-zinc-50 inline-flex items-center gap-1 focus-ring" data-testid="copy-storefront">
            <Copy className="h-3 w-3" /> Copy
          </button>
          <a href={url} target="_blank" rel="noopener noreferrer" className="rounded-full ashra-gradient-bg px-4 py-2 text-xs font-semibold text-white inline-flex items-center gap-1 shadow-sm focus-ring">
            Open <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </div>

      {/* AI Insights — operational intelligence */}
      {insights && (
        <div className="surface p-5 mb-6" data-testid="commerce-insights">
          <div className="flex items-center gap-2 mb-4">
            <div className="h-8 w-8 rounded-xl bg-ai-soft grid place-items-center text-ai"><Sparkles className="h-4 w-4" /></div>
            <h3 className="font-display text-sm font-bold text-ink">Ashra Intelligence</h3>
            <span className="pill pill-ai ml-auto"><span className="pulse-dot" /> Live</span>
          </div>
          <div className="grid sm:grid-cols-2 gap-3 mb-4">
            <KPI label="Revenue 30d" value={`$${Math.round(insights.summary.revenue_30d).toLocaleString()}`} tone="success" />
            <KPI label="Recovered this week" value={`$${insights.summary.recovered_this_week.toLocaleString()}`} tone="ai" />
          </div>
          <ul className="space-y-2">
            {insights.insights.map((i, idx) => {
              const Icon = ICONS[i.icon] || Sparkles;
              return (
                <li key={idx} className={`rounded-xl border ${TONES[i.tone] || TONES.info} px-3 py-2 text-sm flex items-start gap-2`}>
                  <Icon className="h-4 w-4 mt-0.5 shrink-0" /> <span className="text-ink-soft">{i.text}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Sections grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {sections.map((s) => (
          <Link key={s.to} to={s.to} className="surface p-5 hover:shadow-md transition group" data-testid={`storefront-tile-${s.label.toLowerCase()}`}>
            <div className={`h-10 w-10 rounded-xl bg-gradient-to-br ${s.gradient} grid place-items-center text-white mb-3 shadow-sm`}>
              <s.icon className="h-5 w-5" />
            </div>
            <div className="flex items-center justify-between">
              <p className="font-display font-bold text-ink">{s.label}</p>
              <ArrowRight className="h-4 w-4 text-meta group-hover:text-ai transition" />
            </div>
            <p className="text-sm text-meta mt-1">{s.count} live</p>
          </Link>
        ))}
        <div className="surface p-5 bg-gradient-to-br from-violet-50 to-pink-50 border-violet-200">
          <Users className="h-7 w-7 text-ai mb-3" />
          <p className="font-display font-bold text-ink">Customers</p>
          <p className="text-sm text-meta mt-1">Buyers, members, learners</p>
          <Link to="/commerce/customers" className="mt-2 inline-flex text-xs font-bold text-ai hover:underline">View all →</Link>
        </div>
      </div>
    </div>
  );
}

function KPI({ label, value, tone }) {
  const toneCls = tone === "success" ? "text-success" : tone === "ai" ? "text-ai" : "text-ink";
  return (
    <div className="rounded-2xl bg-zinc-50 border border-line p-4">
      <p className="text-[11px] uppercase tracking-wider text-meta font-semibold">{label}</p>
      <p className={`mt-1 font-display text-2xl font-extrabold ${toneCls}`}>{value}</p>
    </div>
  );
}
